import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function isSameCell(cell, rowIdx, colIdx) {
    return cell?.rowIdx === rowIdx && cell?.colIdx === colIdx;
}

function hasOwnValue(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
}

export function useGridEditing({ focusCell, selection, isCellEditable } = {}) {
    const activeFocusCell = focusCell ?? selection?.focus ?? null;
    const focusCellRef = useRef(activeFocusCell);
    const [editingState, setEditingState] = useState(null);

    useEffect(() => {
        focusCellRef.current = activeFocusCell;
    }, [activeFocusCell]);

    useEffect(() => {
        if (!editingState) return;
        if (!activeFocusCell || !isSameCell(activeFocusCell, editingState.rowIdx, editingState.colIdx)) {
            setEditingState(null);
        }
    }, [activeFocusCell, editingState]);

    const enterEdit = useCallback(
        (rowIdx, colIdx, options = {}) => {
            const triggerFocusCell = options.focusCell ?? focusCellRef.current;
            if (triggerFocusCell && !isSameCell(triggerFocusCell, rowIdx, colIdx)) return false;
            if (isCellEditable && !isCellEditable(rowIdx, colIdx)) return false;

            setEditingState({
                rowIdx,
                colIdx,
                hasInitialValue: hasOwnValue(options, "initialValue"),
                initialValue: options.initialValue,
            });
            return true;
        },
        [isCellEditable],
    );

    const exitEdit = useCallback(() => {
        setEditingState(null);
    }, []);

    const isEditing = useCallback(
        (rowIdx, colIdx) => isSameCell(editingState, rowIdx, colIdx),
        [editingState],
    );

    const editingCell = useMemo(
        () => (editingState ? { rowIdx: editingState.rowIdx, colIdx: editingState.colIdx } : null),
        [editingState],
    );

    return {
        editingCell,
        enterEdit,
        exitEdit,
        isEditing,
        hasInitialValue: editingState?.hasInitialValue === true,
        initialValue: editingState?.initialValue,
    };
}

export default useGridEditing;
